<template>
  <StatCard
    title="Open work by status"
    subtitle="Everything not yet finished"
    :loading="loading"
    :empty="isEmpty"
    as-of-now
    empty-title="Nothing open"
    empty-hint="Every task is finished. Enjoy the quiet."
  >
    <div ref="chartEl" class="w-full h-64"></div>
  </StatCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import StatCard from './StatCard.vue'

const props = defineProps({
  data: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

// The board's own dot colours, so a status reads identically in both places.
const STATUS_META = {
  pending: { label: 'Pending', color: '#9ca3af' },
  started: { label: 'Started', color: '#3b82f6' },
  paused: { label: 'Paused', color: '#f59e0b' },
  completed: { label: 'Completed', color: '#22c55e' },
  canceled: { label: 'Canceled', color: '#f87171' },
}

const chartEl = ref(null)
let chart = null

const total = computed(() => props.data.reduce((sum, d) => sum + d.count, 0))
const isEmpty = computed(() => !props.loading && total.value === 0)

function render() {
  if (!chartEl.value || isEmpty.value) return
  // Highcharts arrives from a CDN via layouts/_highcharts.html.erb. If that is
  // blocked or still in flight, degrade to an empty chart area rather than
  // throwing and taking the rest of the page down with it.
  if (typeof Highcharts === 'undefined') return
  if (chart) { chart.destroy(); chart = null }

  chart = Highcharts.chart(chartEl.value, {
    chart: { type: 'pie', height: 256, spacing: [12, 12, 12, 12] },
    title: {
      text: `${total.value}<br><span style="font-size:11px;opacity:.6">open</span>`,
      align: 'center', verticalAlign: 'middle', y: 8, useHTML: true,
      style: { fontSize: '22px', fontWeight: '700' },
    },
    tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)' },
    plotOptions: {
      pie: {
        innerSize: '68%', borderWidth: 0, size: '85%',
        dataLabels: { enabled: false },
        showInLegend: true,
      },
    },
    legend: { align: 'center', verticalAlign: 'bottom', itemStyle: { fontWeight: '500' } },
    series: [{
      name: 'Tasks',
      data: props.data.map(d => ({
        name: STATUS_META[d.status]?.label || d.status,
        y: d.count,
        color: STATUS_META[d.status]?.color || '#9ca3af',
      })),
    }],
  })
}

watch(() => props.data, () => nextTick(render), { deep: true })
onMounted(() => { if (!isEmpty.value) nextTick(render) })
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
